import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { CouponTableSkeleton } from "@/components/ui/coupon-table-skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Eye, Plus } from "lucide-react";
import { CreateQuizForm, type CreatedQuizTableRow } from "./CreateQuizForm";
import { QuizApi, type QuizListItem, type QuizListQuestion } from "@/api/giftcard.api";
import { HandleError } from "@/api/axiosClient";
import { useToast } from "@/hooks/use-toast";
import { getFormattedDateTime } from "@/utils";

type QuizRow = CreatedQuizTableRow;

function sortQuestions(qu: QuizListQuestion[]): QuizListQuestion[] {
  return [...qu].sort((a, b) => {
    if (a.order_index !== b.order_index) return a.order_index - b.order_index;
    return a.question_id - b.question_id;
  });
}

function openPublicQuizPage() {
  const url = `${window.location.origin}/quiz`;
  window.open(url, "_blank", "noopener,noreferrer");
}

function mapApiQuizToRow(q: QuizListItem): QuizRow {
  const questions = sortQuestions(q.questions);
  return {
    id: `QZ-${q.id}`,
    quizId: q.id,
    title: q.name,
    description: q.description,
    timeLimitSeconds: q.time_limit_seconds,
    maxScore: q.max_score,
    passingScore: q.passing_score,
    isActive: q.is_active,
    createdBy: q.created_by,
    createdDatetime: q.created_datetime,
    questions: questions.map((qu) => {
      const options = [...qu.options].sort((a, b) => {
        if (a.order_index !== b.order_index) return a.order_index - b.order_index;
        return a.option_id - b.option_id;
      });
      return {
        id: `q-${qu.question_id}`,
        text: qu.question_text,
        difficulty: qu.difficulty,
        questionType: qu.question_type,
        options: options.map((o) => ({
          id: `o-${o.option_id}`,
          text: o.text,
          isCorrect: o.is_correct === 1,
        })),
      };
    }),
  };
}

export function GamificationAllQuizzes() {
  const { toast } = useToast();
  const [quizzes, setQuizzes] = useState<QuizRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [viewRow, setViewRow] = useState<QuizRow | null>(null);

  const fetchQuizzes = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await QuizApi.getAllQuizzes({});
      if (response.error_msg && String(response.error_msg).trim() !== "") {
        toast({
          title: "Error",
          description: String(response.error_msg),
          variant: "destructive",
        });
        setQuizzes([]);
        return;
      }
      const list = response.getAllQuiz ?? [];
      setQuizzes(list.map(mapApiQuizToRow));
    } catch (error) {
      console.error("GetAllQuizzes:", error);
      const errorMessage = HandleError(error);
      toast({
        title: "Error",
        description: errorMessage || "Failed to load quizzes",
        variant: "destructive",
      });
      setQuizzes([]);
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchQuizzes();
  }, [fetchQuizzes]);

  const columns = useMemo(
    () => [
      { header: "Quiz name", accessorKey: "title" },
      { header: "Questions", accessorKey: "questions", cell: (row: QuizRow) => row.questions.length },
      {
        header: "Time limit",
        id: "time",
        cell: (row: QuizRow) => {
          const m = Math.round(row.timeLimitSeconds / 60);
          return `${m} min (${row.timeLimitSeconds}s)`;
        },
      },
      { header: "Max score", accessorKey: "maxScore" },
      { header: "Passing score", accessorKey: "passingScore" },
      {
        header: "Active",
        id: "active",
        cell: (row: QuizRow) =>
          row.isActive === false ? (
            <Badge variant="secondary">Inactive</Badge>
          ) : (
            <Badge className="bg-green-100 text-green-800">Active</Badge>
          ),
      },
      {
        header: "Action",
        id: "actions",
        cell: (row: QuizRow) => (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setViewRow(row)}
            className="flex items-center gap-2"
          >
            <Eye className="h-4 w-4" />
            View
          </Button>
        ),
      },
    ],
    []
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Quiz</h2>
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" onClick={openPublicQuizPage}>
            View
          </Button>
          <Button
            className="bg-orange-600 text-white hover:bg-orange-700"
            onClick={() => setIsCreateOpen(true)}
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Quiz
          </Button>
        </div>
      </div>

      {isLoading ? (
        <CouponTableSkeleton rowCount={5} />
      ) : (
        <DataTable title="" data={quizzes} columns={columns as any} />
      )}

      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Quiz</DialogTitle>
          </DialogHeader>
          <CreateQuizForm
            onSuccess={() => {
              setIsCreateOpen(false);
              fetchQuizzes();
            }}
            onCancel={() => setIsCreateOpen(false)}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewRow} onOpenChange={(open) => !open && setViewRow(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Quiz details</DialogTitle>
          </DialogHeader>
          {viewRow && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-gray-500">Quiz name</div>
                  <div className="font-medium">{viewRow.title}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Status</div>
                  <div>{viewRow.isActive === false ? "Inactive" : "Active"}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Time limit</div>
                  <div>
                    {Math.round(viewRow.timeLimitSeconds / 60)} min ({viewRow.timeLimitSeconds}{" "}
                    s)
                  </div>
                </div>
                {viewRow.createdDatetime ? (
                  <div>
                    <div className="text-sm text-gray-500">Created</div>
                    <div>
                      {getFormattedDateTime(viewRow.createdDatetime) ||
                        viewRow.createdDatetime}
                    </div>
                  </div>
                ) : null}
                <div className="md:col-span-2">
                  <div className="text-sm text-gray-500">Description</div>
                  <div>{viewRow.description || "—"}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Max score</div>
                  <div>{viewRow.maxScore}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Passing score</div>
                  <div>{viewRow.passingScore}</div>
                </div>
                {viewRow.createdBy != null ? (
                  <div>
                    <div className="text-sm text-gray-500">Created by</div>
                    <div>{viewRow.createdBy}</div>
                  </div>
                ) : null}
              </div>
              <div className="pt-2 border-t space-y-4">
                <div className="text-sm font-medium text-gray-700">Questions</div>
                <ol className="list-decimal pl-4 space-y-4">
                  {viewRow.questions.map((q) => (
                    <li key={q.id} className="text-sm space-y-2">
                      <div>
                        <span className="font-medium">{q.text}</span>
                        <span className="text-muted-foreground ml-2">
                          ({q.difficulty} · {q.questionType})
                        </span>
                      </div>
                      <ul className="list-none space-y-1 pl-0">
                        {q.options.map((o) => (
                          <li
                            key={o.id}
                            className={
                              o.isCorrect
                                ? "text-green-700 font-medium"
                                : "text-muted-foreground"
                            }
                          >
                            {o.isCorrect ? "✓ " : "· "}
                            {o.text}
                          </li>
                        ))}
                      </ul>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
